import {
  CreateReceiptRequest,
  ReceiptPortion,
  Product,
  Receipt,
  ScanReceiptResponse,
  SplitMode,
  UserWithProducts,
} from "../../../services/receipt/receiptModel";
import {allocatePortions} from './portionModel';
import {UserResponse} from "../../../services/users/userModel";
import {GroupGuest} from "../../../services/groups/groupModel";
import {
  isGuest, ParticipantKey, participantKey, participantKeys, SplitParticipant,
} from "../../../services/groups/participants";
import {
  fromCents,
  lineTotalCents,
  splitCentsEvenly,
  toCents,
} from "../../../utils/allocation";

export type {SplitMode} from '../../../services/receipt/receiptModel';

/**
 * The only currency the backend can persist today. Detected receipts in any
 * other currency are surfaced to the user and blocked from saving rather than
 * being silently relabelled or converted (see {@link isCurrencySupported}).
 */
export const SUPPORTED_CURRENCY = 'EUR';

export interface ReviewRow {
  id: number;
  name: string;
  /** Fractional quantities are preserved (e.g. weighed goods). */
  quantity: number;
  /** UNIT price. */
  price: number;
  /** Member ids this row is assigned to (item-assignment mode only). */
  memberIds: ParticipantKey[];
  allocations?: ReceiptPortion[];
}

let rowSeq = 0;
export function createRow(partial?: Partial<ReviewRow>): ReviewRow {
  rowSeq += 1;
  return {
    id: rowSeq,
    name: partial?.name ?? '',
    quantity: partial?.quantity ?? 1,
    price: partial?.price ?? 0,
    memberIds: partial?.memberIds ?? [],
    ...(partial?.allocations ? {allocations: partial.allocations} : {}),
  };
}

/**
 * Map an OCR scan response to editable review rows. Unit prices and fractional
 * quantities are preserved verbatim - no multiplication by line totals.
 */
export function scanResponseToRows(response: Pick<ScanReceiptResponse, 'products'>): ReviewRow[] {
  return (response.products ?? []).map((p) =>
    createRow({name: p.name, quantity: p.quantity, price: p.price})
  );
}

/** Only canonical items retain original unit prices and quantities. */
export function receiptToRows(receipt: Receipt): ReviewRow[] {
  return (receipt.items ?? []).map(item => createRow({
    name: item.name, quantity: item.quantity, price: item.price,
    memberIds: [...item.participantKeys],
    ...(item.allocations ? {allocations: item.allocations} : {}),
  }));
}

export function receiptEditError(receipt: Receipt, now = Date.now()): string | undefined {
  if (!receipt.canEdit) return 'This receipt cannot be edited. Only its creator can edit it within 24 hours.';
  if (!Number.isSafeInteger(receipt.id) || (receipt.id ?? 0) <= 0 ||
      !Number.isSafeInteger(receipt.version) || (receipt.version ?? -1) < 0) {
    return 'This receipt is missing its ID or version. Reload it before saving.';
  }
  if (receipt.editableUntil != null) {
    const deadline = Date.parse(receipt.editableUntil);
    if (!Number.isFinite(deadline) || now >= deadline) {
      return 'The 24-hour editing window has expired.';
    }
  }
  if (!receipt.items?.length || !receipt.participantKeys?.length ||
      (receipt.splitMode !== 'equal' && receipt.splitMode !== 'items' && receipt.splitMode !== 'portions')) {
    return 'The original items and split are unavailable. This receipt cannot be edited.';
  }
  return undefined;
}

/** Sum of every row's line total, in cents. */
export function rowsTotalCents(rows: ReviewRow[]): number {
  return rows.reduce((sum, row) => sum + lineTotalCents(row), 0);
}

export function rowsTotal(rows: ReviewRow[]): number {
  return fromCents(rowsTotalCents(rows));
}

/**
 * Currency support guard. `null`/empty means the scan couldn't detect a
 * currency, in which case we assume the supported one. Any explicit non-EUR
 * code is unsupported because the backend does not persist currency yet.
 */
export function isCurrencySupported(currency?: string | null): boolean {
  if (!currency) {
    return true;
  }
  return currency.trim().toUpperCase() === SUPPORTED_CURRENCY;
}

export interface MemberSummary {
  participant: SplitParticipant;
  user?: UserResponse;
  guest?: GroupGuest;
  amountCents: number;
  /**
   * The exact products this member is charged for. Each product's
   * `price * quantity` (quantity is always 1, price is the member's cent-exact
   * share of an original row) sums to `amountCents`, so the backend - which
   * recomputes every entry from its products and ignores any client total -
   * arrives at precisely the figure shown in the UI.
   */
  products: Product[];
}

export interface SplitOutcome {
  perMember: MemberSummary[];
  unassignedRowIds: number[];
  allocatedCents: number;
  totalCents: number;
  /** True when every cent is accounted for and can be saved. */
  balanced: boolean;
}

function cleanName(name: string): string {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : 'Item';
}

/**
 * Compute per-member owed amounts AND the exact per-member product breakdown.
 *
 * Every row's line total is distributed - in integer cents - across the members
 * that share it (all active members in `equal` mode, the assigned members in
 * `items` mode). A member's products therefore always recompute, on the
 * backend, to exactly the amount shown here. Unassigned rows are reported back
 * to the caller and never silently dropped.
 */
export function computeSplit(
  rows: ReviewRow[],
  members: SplitParticipant[],
  mode: SplitMode
): SplitOutcome {
  const perMemberCents: Record<string, number> = {};
  const perMemberProducts: Record<string, Product[]> = {};
  members.forEach((m) => {
    perMemberCents[participantKey(m)] = 0;
    perMemberProducts[participantKey(m)] = [];
  });

  const memberIds = members.map(participantKey);
  const unassignedRowIds: number[] = [];
  let allocatedCents = 0;
  let totalCents = 0;

  rows.forEach((row, index) => {
    const lineCents = lineTotalCents(row);
    totalCents += lineCents;

    if (mode !== 'equal' && row.allocations) {
      const outcome = allocatePortions(row, index, memberIds);
      if (!outcome.complete) unassignedRowIds.push(row.id);
      Object.entries(outcome.perParticipant).forEach(([key, cents]) => {
        if (!memberIds.includes(key)) return;
        perMemberCents[key] += cents;
        if (cents !== 0) perMemberProducts[key].push({name: cleanName(row.name), quantity: 1, price: fromCents(cents)});
      });
      allocatedCents += Object.values(outcome.perParticipant).reduce((sum, cents) => sum + cents, 0);
      return;
    }

    const assignees =
      mode === 'equal'
        ? memberIds
        : participantKeys(row.memberIds).filter((id) => memberIds.includes(id));

    if (assignees.length === 0) {
      // Equal mode with nobody sharing simply produces no allocation; item
      // mode surfaces the row so the user can assign it before saving.
      if (mode !== 'equal') {
        unassignedRowIds.push(row.id);
      }
      return;
    }

    // Rotate the leftover-cent recipient per row so pennies are spread fairly
    // across members instead of always landing on the first person.
    const shares = splitCentsEvenly(lineCents, assignees.length, index);
    assignees.forEach((id, i) => {
      const cents = shares[i];
      perMemberCents[String(id)] += cents;
      if (cents !== 0) {
        perMemberProducts[String(id)].push({
          name: cleanName(row.name),
          quantity: 1,
          price: fromCents(cents),
        });
      }
    });
    allocatedCents += lineCents;
  });

  const perMember: MemberSummary[] = members.map((participant) => ({
    participant,
    ...(isGuest(participant) ? {guest: participant} : {user: participant}),
    amountCents: perMemberCents[participantKey(participant)] ?? 0,
    products: perMemberProducts[participantKey(participant)] ?? [],
  }));

  const sumCents = perMember.reduce((acc, m) => acc + m.amountCents, 0);
  const balanced =
    memberIds.length > 0 &&
    rows.length > 0 &&
    totalCents > 0 &&
    unassignedRowIds.length === 0 &&
    sumCents === totalCents;

  return {perMember, unassignedRowIds, allocatedCents, totalCents, balanced};
}

/**
 * Validate the edited rows before we ever build a save payload. Returns a list
 * of human-readable problems; an empty list means the rows are safe to send.
 * Nothing is silently coerced to zero or invented.
 */
export function validateRows(rows: ReviewRow[]): string[] {
  const errors: string[] = [];
  if (rows.length === 0) {
    errors.push('Add at least one item before saving.');
    return errors;
  }
  rows.forEach((row, index) => {
    const label = row.name.trim() || `Item ${index + 1}`;
    if (!row.name.trim()) {
      errors.push(`Give item ${index + 1} a name.`);
    }
    if (!Number.isFinite(row.quantity) || row.quantity <= 0) {
      errors.push(`"${label}" needs a quantity greater than zero.`);
    }
    if (!Number.isFinite(row.price) || row.price < 0) {
      errors.push(`"${label}" needs a valid price.`);
    }
  });
  if (errors.length === 0 && rowsTotalCents(rows) <= 0) {
    errors.push('The receipt total must be greater than zero.');
  }
  return errors;
}

/**
 * Build the `/receipt/create` payload. Each member entry carries ONLY the
 * cent-exact share products they are responsible for, so the backend recompute
 * (`sum(product.price * quantity)` per entry, and across entries for the
 * receipt total) reproduces exactly the amounts shown in the UI. Members who
 * end up sharing nothing are omitted entirely because the backend rejects
 * entries with no products. `scanId` is only sent for scanned receipts.
 *
 * @throws when the rows are invalid, the split isn't balanced, or the currency
 *         isn't one the backend can persist - callers must gate on the same
 *         checks so this never fires in normal use.
 */
export function buildCreateRequest(params: {
  name: string;
  groupName: string;
  rows: ReviewRow[];
  members: SplitParticipant[];
  mode: SplitMode;
  currency?: string | null;
  scanId?: string;
}): CreateReceiptRequest {
  const {name, groupName, rows, members, mode, currency, scanId} = params;

  const rowErrors = validateRows(rows);
  if (rowErrors.length > 0) {
    throw new Error(rowErrors[0]);
  }
  if (!isCurrencySupported(currency)) {
    throw new Error(
      `Receipts in ${currency} can't be saved yet - only ${SUPPORTED_CURRENCY} is supported.`
    );
  }

  const split = computeSplit(rows, members, mode);
  if (!split.balanced) {
    throw new Error('Every item must be assigned before saving.');
  }

  const entries: UserWithProducts[] = split.perMember
    .filter((summary) => summary.products.length > 0)
    .map((summary) => ({
      ...(summary.guest ? {guest: summary.guest} : {user: summary.user}),
      products: summary.products,
      totalPrice: fromCents(summary.amountCents),
    }));

  if (entries.length === 0) {
    throw new Error('Nobody is sharing this receipt yet.');
  }

  const request: CreateReceiptRequest = {
    name: name.trim(),
    groupName,
    // Only forward a currency the backend understands; otherwise leave it null
    // rather than implying unsupported currency support.
    currency: currency && isCurrencySupported(currency) ? SUPPORTED_CURRENCY : null,
    totalPrice: fromCents(split.totalCents),
    entries,
    splitMode: mode !== 'equal' && rows.some(row => row.allocations != null) ? 'portions' : mode,
    participantKeys: members.map(participantKey),
    items: rows.map(row => ({
      name: row.name.trim(),
      quantity: row.quantity,
      price: row.price,
      participantKeys: mode === 'equal' ? [] : participantKeys(row.allocations ? row.allocations.flatMap(portion => portion.participantKeys) : row.memberIds)
        .filter(key => members.some(member => participantKey(member) === key)),
      ...(mode !== 'equal' && rows.some(item => item.allocations != null) ? {allocations: row.allocations ?? [{quantity: row.quantity, participantKeys: participantKeys(row.memberIds)}]} : {}),
    })),
  };
  if (scanId) {
    request.scanId = scanId;
  }
  return request;
}

export {toCents, fromCents, lineTotalCents};
